import React, { useState } from 'react';

import { 
  User, 
  FileText, 
  Calendar, 
  DollarSign, 
  Save, 
  X, 
  CheckCircle, 
  AlertCircle,
  UserPlus,
  Building,
  Receipt,
  Phone,
  Mail,
  MapPin,
  IdCard,
  Shield,
  TrendingUp,
  Users,
  Upload,
  Download,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';

function AddMember() {
  const [formData, setFormData] = useState({
    memberId: '',
    fullName: '',
    gender: 'male',
    dateOfBirth: '',
    age: '',
    phoneNumber: '',
    email: '',
    address: '',
    idNumber: '',
    occupation: '',
    joinDate: new Date().toISOString().split('T')[0],
    emergencyContactName: '',
    emergencyContactPhone: '',
    
    // Financial Information
    shareContribution: '',
    monthlyContribution: '',
    totalContribution: 0,
    shareholderLevel: 'basic',
    
    // Payment Details
    paymentMethod: 'cash',
    receiptNumber: '',
    transactionDate: new Date().toISOString().split('T')[0],
    
    // Documents
    profilePhoto: null,
    idDocument: null,
    signature: null,
    
    // Additional Info
    nominatedBy: '',
    department: '',
    membershipType: 'regular',
    status: 'active',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [members, setMembers] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('add'); // 'add', 'view', 'edit'

  // Cooperative Union data
  const shareholderLevels = [
    { value: 'basic', label: 'Basic (1-10 shares)', minShares: 1, maxShares: 10 },
    { value: 'silver', label: 'Silver (11-50 shares)', minShares: 11, maxShares: 50 },
    { value: 'gold', label: 'Gold (51-100 shares)', minShares: 51, maxShares: 100 },
    { value: 'platinum', label: 'Platinum (100+ shares)', minShares: 101, maxShares: 1000 }
  ];

  const paymentMethods = [
    'Cash', 'Bank Transfer', 'Mobile Money', 'Check', 'Payroll Deduction'
  ];

  const membershipTypes = [
    'Regular', 'Associate', 'Honorary', 'Corporate'
  ];

  const occupations = [
    'Farmer', 'Teacher', 'Healthcare Worker', 'Civil Servant', 'Business Owner',
    'Student', 'Retired', 'Self-Employed', 'Private Sector', 'Other'
  ];

  const departments = [
    'Agriculture', 'Education', 'Healthcare', 'Finance', 'Administration',
    'Marketing', 'Operations', 'Technology', 'Human Resources'
  ];

  const generateMemberId = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(10000 + Math.random() * 90000);
    return `CU/${year}/${random}`;
  };

  const calculateAge = (dateString) => {
    if (!dateString) return '';
    const birthDate = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (name === 'dateOfBirth') {
      const age = calculateAge(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        age: age
      }));
    } else if (type === 'file') {
      const file = files[0];
      if (file) {
        if (name === 'profilePhoto') {
          const reader = new FileReader();
          reader.onloadend = () => {
            setPreviewImage(reader.result);
          };
          reader.readAsDataURL(file);
        }
        setFormData(prev => ({
          ...prev,
          [name]: file
        }));
      }
    } else if (name === 'shareContribution' || name === 'monthlyContribution') {
      // Auto-calculate total contribution
      const shareValue = name === 'shareContribution' ? parseFloat(value) || 0 : parseFloat(formData.shareContribution) || 0;
      const monthlyValue = name === 'monthlyContribution' ? parseFloat(value) || 0 : parseFloat(formData.monthlyContribution) || 0;
      const total = shareValue + monthlyValue;
      
      // Auto-assign shareholder level based on shares
      let level = 'basic';
      if (shareValue >= 101) level = 'platinum';
      else if (shareValue >= 51) level = 'gold';
      else if (shareValue >= 11) level = 'silver';
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        totalContribution: total,
        shareholderLevel: level
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (parseInt(formData.age) < 18) newErrors.age = 'Member must be at least 18 years old';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.shareContribution || parseFloat(formData.shareContribution) <= 0) 
      newErrors.shareContribution = 'Share contribution is required';
    if (!formData.receiptNumber.trim()) newErrors.receiptNumber = 'Receipt number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Generate member ID if not exists
    const memberId = formData.memberId || generateMemberId();
    
    // Calculate member benefits based on shareholder level
    const levelInfo = shareholderLevels.find(l => l.value === formData.shareholderLevel);
    const votingPower = levelInfo ? Math.min(Math.floor(parseFloat(formData.shareContribution) / 10), 10) : 1;
    const dividendRate = levelInfo ? 
      { basic: 0.05, silver: 0.07, gold: 0.10, platinum: 0.15 }[formData.shareholderLevel] : 0.05;

    const memberData = {
      ...formData,
      memberId: memberId,
      votingPower: votingPower,
      dividendRate: dividendRate,
      registrationDate: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      membershipNumber: `CU${Date.now().toString().slice(-6)}`,
      // Calculate share value (assuming each share = 100 currency units)
      shareValue: parseFloat(formData.shareContribution) * 100,
      totalShares: parseFloat(formData.shareContribution)
    };

    // Simulate API call
    setTimeout(() => {
      console.log('Member added:', memberData);
      setMembers(prev => [memberData, ...prev]);
      setIsSubmitting(false);
      
      alert(`Member added successfully!\nMember ID: ${memberId}\nReceipt: ${formData.receiptNumber}`);
      
      // Reset form for next entry
      resetForm();
    }, 1500);
  };

  const resetForm = () => {
    setFormData({
      memberId: '',
      fullName: '',
      gender: 'male',
      dateOfBirth: '',
      age: '',
      phoneNumber: '',
      email: '',
      address: '',
      idNumber: '',
      occupation: '',
      joinDate: new Date().toISOString().split('T')[0],
      emergencyContactName: '',
      emergencyContactPhone: '',
      shareContribution: '',
      monthlyContribution: '',
      totalContribution: 0,
      shareholderLevel: 'basic',
      paymentMethod: 'cash',
      receiptNumber: '',
      transactionDate: new Date().toISOString().split('T')[0],
      profilePhoto: null,
      idDocument: null,
      signature: null,
      nominatedBy: '',
      department: '',
      membershipType: 'regular',
      status: 'active',
      notes: ''
    });
    setPreviewImage(null);
    setErrors({});
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleEditMember = (member) => {
    setFormData(member);
    setViewMode('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteMember = (memberId) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      setMembers(prev => prev.filter(m => m.memberId !== memberId));
    }
  };

  const filteredMembers = members.filter(member =>
    member.fullName.toLaowerCase().includes(searchTerm.toLowerCase()) ||
    member.memberId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phoneNumber.includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
      
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Cooperative Union Member Registration</h1>
                <p className="text-gray-600">Add new members and manage cooperative shares</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">
                  Total Members: {members.length}
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">
                  Total Shares: {members.reduce((sum, m) => sum + (parseFloat(m.shareContribution) || 0), 0)}
                </span>
              </div>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setViewMode('add')}
              className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'add' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <UserPlus className="w-4 h-4 inline mr-2" />
              Add New Member
            </button>
            <button
              onClick={() => setViewMode('view')}
              className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'view' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <Eye className="w-4 h-4 inline mr-2" />
              View Members ({members.length})
            </button>
          </div>
        </div>

        {/* Add/Edit Member Form */}
        {(viewMode === 'add' || viewMode === 'edit') && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                {viewMode === 'edit' ? 'Edit Member Information' : 'Member Registration Form'}
              </h2>
              <button
                onClick={resetForm}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                <X className="w-4 h-4" />
                Clear Form
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Personal Information Column */}
                <div className="space-y-6">
                  <div className="bg-blue-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Personal Information
                    </h3>
                    
                    {/* Member ID */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-1">
                          <IdCard className="w-4 h-4" />
                          Member ID
                        </span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          name="memberId"
                          value={formData.memberId}
                          onChange={handleChange}
                          placeholder="Auto-generated"
                          className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          readOnly
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, memberId: generateMemberId() }))}
                          className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                        >
                          Generate
                        </button>
                      </div>
                    </div>

                    {/* Full Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="Enter full name"
                        className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.fullName ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.fullName && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.fullName}
                        </p>
                      )}
                    </div>

                    {/* Gender & Date of Birth */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Gender *
                        </label>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date of Birth *
                        </label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.dateOfBirth ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                      </div>
                    </div>

                    {/* Age (auto-calculated) */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Age
                      </label>
                      <input
                        type="text"
                        name="age"
                        value={formData.age}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg"
                      />
                      {errors.age && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.age}
                        </p>
                      )}
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            Phone Number *
                          </span>
                        </label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          placeholder="Enter phone number"
                          className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.phoneNumber ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            Email (Optional)
                          </span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            Address *
                          </span>
                        </label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          rows="2"
                          placeholder="Enter full address"
                          className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.address ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Profile Photo Upload */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Photo</h3>
                    <div className="flex flex-col items-center justify-center">
                      {previewImage ? (
                        <div className="relative mb-4">
                          <img
                            src={previewImage}
                            alt="Profile preview"
                            className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setPreviewImage(null);
                              setFormData(prev => ({ ...prev, profilePhoto: null }));
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mb-4">
                          <User className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      <label className="cursor-pointer">
                        <div className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center">
                          <Upload className="w-4 h-4 inline mr-2" />
                          Upload Photo
                        </div>
                        <input
                          type="file"
                          name="profilePhoto"
                          accept="image/*"
                          onChange={handleChange}
                          className="hidden"
                        />
                      </label>
                      <p className="text-sm text-gray-500 mt-2">Max size: 2MB</p>
                    </div>
                  </div>
                </div>

                {/* Financial Information Column */}
                <div className="space-y-6">
                  <div className="bg-green-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Financial Information
                    </h3>

                    {/* Share Contribution */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Share Contribution *
                        <span className="text-xs text-gray-500 ml-2">(Minimum 1 share required)</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          $
                        </span>
                        <input
                          type="number"
                          name="shareContribution"
                          value={formData.shareContribution}
                          onChange={handleChange}
                          placeholder="0.00"
                          min="1"
                          step="1"
                          className={`w-full pl-10 pr-4 py-3 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                            errors.shareContribution ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                      </div>
                      {errors.shareContribution && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.shareContribution}
                        </p>
                      )}
                    </div>

                    {/* Monthly Contribution */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Contribution (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          $
                        </span>
                        <input
                          type="number"
                          name="monthlyContribution"
                          value={formData.monthlyContribution}
                          onChange={handleChange}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    </div>

                    {/* Shareholder Level */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          Shareholder Level
                        </span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {shareholderLevels.map(level => (
                          <label
                            key={level.value}
                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${
                              formData.shareholderLevel === level.value
                                ? 'bg-green-100 border-green-500'
                                : 'bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div>
                              <span className="font-medium text-gray-800">{level.label}</span>
                              <p className="text-xs text-gray-500">
                                {level.minShares}-{level.maxShares} shares
                              </p>
                            </div>
                            <input
                              type="radio"
                              name="shareholderLevel"
                              value={level.value}
                              checked={formData.shareholderLevel === level.value}
                              onChange={handleChange}
                              className="text-green-600"
                            />
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Total Contribution Display */}
                    <div className="bg-white rounded-lg border border-gray-300 p-4 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Total Contribution:</span>
                        <span className="text-xl font-bold text-green-600">
                          {formatCurrency(formData.totalContribution)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Includes share contribution + monthly contribution
                      </div>
                    </div>

                    {/* Benefits Summary */}
                    <div className="bg-blue-50 rounded-lg p-3">
                      <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Member Benefits
                      </h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Voting rights in general meetings</li>
                        <li>• Dividend payments (quarterly)</li>
                        <li>• Access to cooperative loans</li>
                        <li>• Training and capacity building</li>
                        <li>• Annual general meeting participation</li>
                      </ul>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="bg-purple-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5" />
                      Additional Information
                    </h3>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Occupation
                        </label>
                        <select
                          name="occupation"
                          value={formData.occupation}
                          onChange={handleChange}
                          className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select Occupation</option>
                          {occupations.map(occ => (
                            <option key={occ} value={occ}>{occ}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Department
                        </label>
                        <select
                          name="department"
                          value={formData.department}
                          onChange={handleChange}
                          className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select Department</option>
                          {departments.map(dept => (
                            <option key={dept} value={dept}>{dept}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Membership Type
                      </label>
                      <select
                        name="membershipType"
                        value={formData.membershipType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        {membershipTypes.map(type => (
                          <option key={type} value={type.toLowerCase()}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nominated By (Optional)
                      </label>
                      <input
                        type="text"
                        name="nominatedBy"
                        value={formData.nominatedBy}
                        onChange={handleChange}
                        placeholder="Name of nominating member"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (Optional)
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Any additional notes..."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment & Documents Column */}
                <div className="space-y-6">
                  <div className="bg-amber-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-amber-800 mb-4 flex items-center gap-2">
                      <Receipt className="w-5 h-5" />
                      Payment Details
                    </h3>

                    {/* Receipt Number */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Receipt Number *
                      </label>
                      <input
                        type="text"
                        name="receiptNumber"
                        value={formData.receiptNumber}
                        onChange={handleChange}
                        placeholder="e.g., RC-CU-2024-001"
                        className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
                          errors.receiptNumber ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.receiptNumber && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.receiptNumber}
                        </p>
                      )}
                    </div>

                    {/* Payment Method */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Method
                      </label>
                      <select
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        {paymentMethods.map(method => (
                          <option key={method} value={method.toLowerCase()}>{method}</option>
                        ))}
                      </select>
                    </div>

                    {/* Transaction Date */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Transaction Date
                        </span>
                      </label>
                      <input
                        type="date"
                        name="transactionDate"
                        value={formData.transactionDate}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    {/* Join Date */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Membership Join Date
                      </label>
                      <input
                        type="date"
                        name="joinDate"
                        value={formData.joinDate}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    {/* Receipt Preview */}
                    <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-4">
                      <h4 className="font-medium text-gray-800 mb-3 text-center">Payment Receipt</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Receipt No:</span>
                          <span className="font-medium">{formData.receiptNumber || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Member:</span>
                          <span className="font-medium">{formData.fullName || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Amount:</span>
                          <span className="font-medium text-green-600">
                            {formatCurrency(formData.totalContribution)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date:</span>
                          <span className="font-medium">{formData.transactionDate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Payment Method:</span>
                          <span className="font-medium capitalize">{formData.paymentMethod}</span>
                        </div>
                        <div className="pt-2 border-t border-gray-200 mt-2 text-center">
                          <span className="text-xs text-gray-500">Cooperative Union Official Receipt</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="bg-red-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Emergency Contact
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contact Name
                        </label>
                        <input
                          type="text"
                          name="emergencyContactName"
                          value={formData.emergencyContactName}
                          onChange={handleChange}
                          placeholder="Full name of emergency contact"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contact Phone
                        </label>
                        <input
                          type="tel"
                          name="emergencyContactPhone"
                          value={formData.emergencyContactPhone}
                          onChange={handleChange}
                          placeholder="Emergency phone number"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Documents Upload */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Required Documents</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ID Document (PDF/Image)
                        </label>
                        <label className="cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-700">
                              {formData.idDocument ? formData.idDocument.name : 'Upload ID Document'}
                            </span>
                          </div>
                          <input
                            type="file"
                            name="idDocument"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleChange}
                            className="hidden"
                          />
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Signature (Image)
                        </label>
                        <label className="cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-700">
                              {formData.signature ? formData.signature.name : 'Upload Signature'}
                            </span>
                          </div>
                          <input
                            type="file"
                            name="signature"
                            accept="image/*"
                            onChange={handleChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="sticky top-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          {viewMode === 'edit' ? 'Update Member Record' : 'Save Member Registration'}
                        </>
                      )}
                    </button>

                    {/* Status Selection */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Member Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                        <option value="graduated">Graduated</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* View Members Section */}
        {viewMode === 'view' && members.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Registered Members ({members.length})
              </h2>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-64 px-4 py-2 pl-10 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
                <button
                  onClick={() => {
                    const csvContent = [
                      ['Member ID', 'Name', 'Phone', 'Shares', 'Contribution', 'Level', 'Join Date'],
                      ...members.map(m => [
                        m.memberId,
                        m.fullName,
                        m.phoneNumber,
                        m.shareContribution,
                        m.totalContribution,
                        m.shareholderLevel,
                        m.joinDate
                      ])
                    ].map(row => row.join(',')).join('\n');
                    
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `cooperative_members_${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Members Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shares
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contribution
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredMembers.map((member, index) => (
                    <tr key={member.memberId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono text-sm font-medium text-blue-600">
                          {member.memberId}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{member.fullName}</div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {member.phoneNumber}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {member.shareContribution} shares
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatCurrency(member.totalContribution)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                          member.shareholderLevel === 'platinum' ? 'bg-purple-100 text-purple-800' :
                          member.shareholderLevel === 'gold' ? 'bg-yellow-100 text-yellow-800' :
                          member.shareholderLevel === 'silver' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {member.shareholderLevel}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          member.status === 'active' ? 'bg-green-100 text-green-800' :
                          member.status === 'inactive' ? 'bg-red-100 text-red-800' :
                          member.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {member.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditMember(member)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              // View member details
                              alert(`Member Details:\n\nName: ${member.fullName}\nID: ${member.memberId}\nShares: ${member.shareContribution}\nLevel: ${member.shareholderLevel}\nPhone: ${member.phoneNumber}\nEmail: ${member.email}`);
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteMember(member.memberId)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredMembers.length === 0 && (
              <div className="text-center py-8">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No members found</h3>
                <p className="text-gray-500">
                  {searchTerm ? 'Try a different search term' : 'Add your first member to get started'}
                </p>
              </div>
            )}
          </div>
        )}

        {viewMode === 'view' && members.length === 0 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
              <UserPlus className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Members Registered Yet</h3>
            <p className="text-gray-600 mb-6">Start by adding your first cooperative union member</p>
            <button
              onClick={() => setViewMode('add')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <UserPlus className="w-5 h-5 inline mr-2" />
              Add First Member
            </button>
          </div>
        )}

        {/* Summary Stats */}
        {members.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Members</p>
                  <p className="text-2xl font-bold text-gray-800">{members.length}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Shares</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {members.reduce((sum, m) => sum + (parseFloat(m.shareContribution) || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Capital</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {formatCurrency(members.reduce((sum, m) => sum + (parseFloat(m.totalContribution) || 0), 0))}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Members</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {members.filter(m => m.status === 'active').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddMember;